/**
 * Screen 12 — sub-users.
 *
 * A company administrator creates users with their own logins; the invitee sets
 * their own password from an emailed link, so no password is ever transmitted by
 * the creator.
 *
 * Users are **disabled, never deleted**. Every DeCA records who created and who
 * amended it, and those references have to stay resolvable for the life of the
 * document — that is what makes the Historial's `usuario` column meaningful a
 * year later.
 *
 * Asked for on 2026-09-30, after TRANSPORTALIS created an administrator under the
 * wrong name and disabled it: a user can be renamed and a disabled one reactivated,
 * and a disabled row offers no letter, since a disabled account cannot redeem a
 * link (`docs/emails/2026-09-30-deca-edit-user-names-request.md`).
 *
 * And since Addendum No. 3, Point 7, a user's role can be changed — «Cambiar rol» — which is how a
 * member becomes a Gestor Individual.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { unwrap, type EmailDelivery, type Role, type User } from '@deca/api'

import { api } from '../app/api.ts'
import {
  apiErrorMessage,
  invalidEmailMessage,
  undeliverableEmailMessage,
  undeliverableResendMessage,
} from '../app/errors.ts'
import { qk, useOrganisation, useUsers } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { useOrganisationChoice } from '../app/useOrganisationChoice.ts'
import { abilitiesFor, assignableRoles, roleKey } from '../deca/roles.ts'
import { emailAddressRules } from '../deca/emailAddress.ts'
import { formatDateTime } from '../deca/format.ts'
import { mailLineFor, mailPollInterval, WATCH_AFTER_SEND_MS } from '../deca/emailDelivery.ts'
import { EmailDeliveryDialog, EmailDeliveryLine, UserAddress } from '../ui/EmailDelivery.tsx'
import { AdminShell } from './AdminShell.tsx'
import { Button, Card, Chip, NoAccessState, Notice } from '../ui/primitives.tsx'
import { DataTable, Pagination, type Column } from '../ui/DataTable.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { SelectInput, TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'
import { useStepBackPastEnd } from '../ui/useStepBackPastEnd.ts'
import { PAGE_DEFAULT, pagedSearchSchema, PER_PAGE } from './searchSchemas.ts'

interface UserFormValues {
  fullName: string
  email: string
  role: Role
  locale: 'es' | 'en'
}

export function AdminUsersScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const client = useQueryClient()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  // Which company these users belong to: the one a platform role has chosen, otherwise the
  // caller's own. The server resolves it exactly the same way, from `X-Organisation-Id`
  // (`backend/src/http/routes/admin.ts`'s `orgOf`), so the screen and the write agree.
  const { organisation: context } = useOrganisationChoice()
  const organisationId = context?.id ?? principal?.organisation.id

  const search = pagedSearchSchema.parse(useSearch({ strict: false }))
  // Until when to keep asking after the office sends a letter (#114). A ref, not state: the poll
  // rule reads it at each fetch, and setting it must not render anything by itself.
  const watchUntil = useRef(0)
  const list = useUsers(
    { page: search.page ?? PAGE_DEFAULT, perPage: PER_PAGE, ...(search.q ? { q: search.q } : {}) },
    abilities.administer,
    (users) => mailPollInterval(users, Date.now(), watchUntil.current),
  )
  // A page past the end shows the last page, not «Sin resultados» (#165).
  useStepBackPastEnd(list.data?.meta, search.page ?? PAGE_DEFAULT, (page) => {
    void navigate({ to: '/admin/usuarios', search: { ...search, page }, replace: true })
  })
  // The full organisation, fetched only for its `settings.locale` — the reduced
  // `principal.organisation` from the auth contract does not carry it. Must sit above
  // the `!abilities.administer` early return below, or the hook order breaks on re-render.
  //
  // It follows the chosen company rather than the signed-in one, because this locale is the
  // default for the invitation the new user receives: taking ASGTRANS's would write to a client
  // company's staff in whichever language ASGTRANS happens to work in.
  const organisation = useOrganisation(abilities.administer ? organisationId : undefined)

  const [createOpen, setCreateOpen] = useState(false)
  // The row being edited, not a boolean: the dialog needs the address it is replacing, and a
  // separate `open` flag plus a separate `row` is how the two drift apart on close.
  const [changingEmail, setChangingEmail] = useState<{ id: string; email: string } | null>(null)
  // The same pattern for the rename: the row itself, carrying the name being corrected.
  const [renaming, setRenaming] = useState<{ id: string; email: string; fullName: string } | null>(null)
  // The row whose role is being changed (Addendum No. 3, Point 7): how a member becomes a Gestor
  // Individual, or stops being one.
  const [changingRole, setChangingRole] = useState<{ id: string; email: string; role: Role } | null>(null)
  // The row whose latest letter is open, with the letter itself — kept whole so the dialog can
  // hand the row to "Cambiar el correo" without a second lookup.
  const [viewingMail, setViewingMail] = useState<{ user: User; delivery: EmailDelivery } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!abilities.administer) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  /** Every action here that sends a letter: keep the list asking until its outcome shows. */
  function watchMail() {
    watchUntil.current = Date.now() + WATCH_AFTER_SEND_MS
  }

  async function act(action: () => Promise<unknown>, message: string, sendsMail = false) {
    setError(null)
    try {
      await action()
      if (sendsMail) watchMail()
      void client.invalidateQueries({ queryKey: qk.users() })
      toast.success(message)
    } catch (caught) {
      // A resend to an address no letter can reach (#141): which domain, and who can change it.
      setError(undeliverableResendMessage(caught, t, abilities.platformAdmin) ?? apiErrorMessage(caught, t))
    }
  }

  /**
   * Brings a disabled user back. The notice follows the status the server answers with, not the
   * one the office might expect: an account that never signed in comes back INVITED, and its next
   * step is the invitation, which the row now offers.
   */
  async function reactivate(row: User) {
    if (!window.confirm(t('admin.users.reactivateConfirm'))) return
    setError(null)
    try {
      const updated = await unwrap(
        api.PATCH('/api/v1/users/{userId}', { params: { path: { userId: row.id } }, body: { status: 'ACTIVE' } }),
      )
      void client.invalidateQueries({ queryKey: qk.users() })
      toast.success(
        updated.status === 'INVITED' ? t('admin.users.reactivatedInvited') : t('admin.users.reactivatedActive'),
      )
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    }
  }

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: t('admin.users.fullName'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 0 }}>
          <span style={{ fontWeight: 600 }}>{row.fullName}</span>
          <UserAddress user={row} canChangeEmail={abilities.platformAdmin} />
        </div>
      ),
    },
    {
      key: 'role',
      header: t('admin.users.role'),
      render: (row) => (
        <span className={row.role === 'SUPER_ADMIN' ? 'role-badge role-badge--super' : 'role-badge'}>
          {t(roleKey(row.role))}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('admin.users.status'),
      render: (row) => (
        <div className="stack-sm" style={{ gap: 4, alignItems: 'flex-start' }}>
          <Chip
            tone={row.status === 'ACTIVE' ? 'success' : row.status === 'INVITED' ? 'info' : 'neutral'}
            icon={row.status === 'ACTIVE' ? 'checkCircle' : row.status === 'INVITED' ? 'clock' : 'ban'}
            muted={row.status === 'DISABLED'}
          >
            {t(`admin.users.status${row.status}`)}
          </Chip>
          {mailLineFor(row) ? (
            <EmailDeliveryLine
              delivery={mailLineFor(row)!}
              onOpen={() => setViewingMail({ user: row, delivery: mailLineFor(row)! })}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 'lastLogin',
      header: t('admin.users.lastLogin'),
      numeric: true,
      render: (row) =>
        row.lastLoginAt ? formatDateTime(row.lastLoginAt, i18n.language) : t('common.never'),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'right',
      render: (row) => (
        <div className="table__actions">
          {/* No letter to a disabled account: it cannot redeem a link, and the server refuses to mint
              one (409 USER_DISABLED). The button names what it sends, since the server sends an
              invited user the invitation and an active one the reset. */}
          {row.status !== 'DISABLED' ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void act(
                  () =>
                    unwrap(
                      api.POST('/api/v1/users/{userId}/password-reset', {
                        params: { path: { userId: row.id } },
                      }),
                    ),
                  row.status === 'INVITED' ? t('admin.users.inviteResent') : t('admin.users.resetSent'),
                  true,
                )
              }
            >
              {row.status === 'INVITED' ? t('admin.users.resendInvite') : t('admin.users.sendReset')}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setRenaming(row)}>
            {t('admin.users.rename')}
          </Button>
          {/* Never on your own row: the server refuses it (CANNOT_CHANGE_OWN_ROLE), and a button
              that can only fail is not an action. */}
          {row.id !== principal?.id ? (
            <Button size="sm" variant="ghost" onClick={() => setChangingRole(row)}>
              {t('admin.users.changeRole')}
            </Button>
          ) : null}
          {abilities.platformAdmin ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setChangingEmail(row)}
            >
              {t('admin.users.changeEmail')}
            </Button>
          ) : null}
          {row.status !== 'DISABLED' ? (
            <Button
              size="sm"
              variant="ghost"
              icon="ban"
              onClick={() => {
                if (!window.confirm(t('admin.users.disableConfirm'))) return
                void act(
                  () =>
                    unwrap(api.DELETE('/api/v1/users/{userId}', { params: { path: { userId: row.id } } })),
                  t('admin.users.disabled'),
                )
              }}
            >
              {t('admin.users.disable')}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" icon="refresh" onClick={() => void reactivate(row)}>
              {t('admin.users.reactivate')}
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <AdminShell
      heading={t('admin.users.titleScoped', {
        // The heading's whole job is to say whose users these are, so it names the chosen
        // company when there is one. Left on the principal it would print ASGTRANS over a client
        // company's list — the same wrong-company failure as #60, in the one line meant to prevent it.
        organisation: context?.name ?? principal?.organisation.legalName ?? '',
      })}
      subtitle={t('admin.users.subtitle')}
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card
        flush
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>
            {t('admin.users.create')}
          </Button>
        }
      >
        <DataTable
          rows={list.data?.data}
          columns={columns}
          rowKey={(row) => row.id}
          caption={t('admin.users.title')}
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
        {list.data?.meta ? (
          <Pagination
            page={list.data.meta.page}
            perPage={list.data.meta.perPage}
            total={list.data.meta.total}
            totalPages={list.data.meta.totalPages}
            onPage={(page) => void navigate({ to: '/admin/usuarios', search: { ...search, page } })}
          />
        ) : null}
      </Card>

      <EmailDeliveryDialog
        delivery={viewingMail?.delivery ?? null}
        onClose={() => setViewingMail(null)}
        onChangeEmail={
          abilities.platformAdmin && viewingMail
            ? () => {
                setChangingEmail(viewingMail.user)
                setViewingMail(null)
              }
            : undefined
        }
      />

      <ChangeEmailDialog
        user={changingEmail}
        onClose={() => setChangingEmail(null)}
        onChanged={() => {
          watchMail()
          void client.invalidateQueries({ queryKey: qk.users() })
          toast.success(t('admin.users.emailChanged'))
        }}
      />

      <RenameUserDialog
        user={renaming}
        onClose={() => setRenaming(null)}
        onRenamed={() => {
          void client.invalidateQueries({ queryKey: qk.users() })
          toast.success(t('admin.users.renamed'))
        }}
      />

      <ChangeRoleDialog
        user={changingRole}
        roles={assignableRoles(abilities, context !== null)}
        onClose={() => setChangingRole(null)}
        onChanged={() => {
          void client.invalidateQueries({ queryKey: qk.users() })
          toast.success(t('admin.users.roleChanged'))
        }}
      />

      <CreateUserDialog
        open={createOpen}
        roles={assignableRoles(abilities, context !== null)}
        locale={organisation.data?.settings?.locale}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          watchMail()
          void client.invalidateQueries({ queryKey: qk.users() })
          toast.success(t('admin.users.created'))
        }}
      />
    </AdminShell>
  )
}

/**
 * Change the address a user signs in with — `SUPER_ADMIN` only.
 *
 * The affordance is gated on `abilities.platformAdmin`, and the server refuses anyone else
 * regardless (`requirePlatformAdmin` on `POST /users/{userId}/email`). The gate here decides
 * whether a button is drawn, not whether the change is allowed; if the two ever disagree the
 * result is a button that answers 403, which the test alongside this file pins.
 *
 * Deliberately its own dialog rather than a field on the create form or an inline edit: this is
 * the one user-administration act a company admin may not perform, and an affordance that looks
 * like the others invites folding it into them later.
 *
 * Exported for `src/test/address-dialogs.test.tsx`, as is `CreateUserDialog` below.
 */
export function ChangeEmailDialog({
  user,
  onClose,
  onChanged,
}: {
  /** The row being edited, or null when the dialog is closed. Carries the address being replaced. */
  user: { id: string; email: string } | null
  onClose: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const form = useForm<{ email: string }>({ defaultValues: { email: '' } })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(values: { email: string }) {
    if (!user) return
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/users/{userId}/email', {
          params: { path: { userId: user.id } },
          body: { email: values.email },
        }),
      )
      form.reset({ email: '' })
      onChanged()
      onClose()
    } catch (caught) {
      // A domain that takes no mail, or an address the route schema refuses, is said on the field,
      // which is what has to change. A 409 arrives as the server's own message — the address
      // belongs to somebody else, which is a thing the administrator can act on rather than a
      // validation detail.
      const onAddress = undeliverableEmailMessage(caught, t) ?? invalidEmailMessage(caught, t, 'body/email')
      if (onAddress) form.setError('email', { message: onAddress }, { shouldFocus: true })
      else setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={user !== null}
      busy={busy}
      title={t('admin.users.changeEmail')}
      subtitle={user?.email}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Notice tone="info">{t('admin.users.changeEmailHelp')}</Notice>

      <div className="form-grid form-grid--full">
        <TextInput
          label={t('admin.users.email')}
          type="email"
          required
          error={form.formState.errors.email?.message}
          {...form.register('email', emailAddressRules(t))}
        />
      </div>
    </Dialog>
  )
}

/**
 * Correct a user's name, for company administrators and the platform alike: the server guards the
 * rename with `org:admin`, as it does the rest of user administration. It is a different call from
 * «Cambiar el correo», which only the platform may use. The address, the role and the password stay
 * as they are, and documents keep the name they recorded at the time.
 *
 * Prefilled with the name being corrected, since the usual fix is a letter or a surname rather than
 * a whole new name.
 */
function RenameUserDialog({
  user,
  onClose,
  onRenamed,
}: {
  /** The row being renamed, or null when the dialog is closed. */
  user: { id: string; email: string; fullName: string } | null
  onClose: () => void
  onRenamed: () => void
}) {
  const { t } = useTranslation()
  const form = useForm<{ fullName: string }>({ defaultValues: { fullName: '' } })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      form.reset({ fullName: user.fullName })
      setError(null)
    }
  }, [user, form])

  async function submit(values: { fullName: string }) {
    if (!user) return
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.PATCH('/api/v1/users/{userId}', {
          params: { path: { userId: user.id } },
          body: { fullName: values.fullName.trim() },
        }),
      )
      onRenamed()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={user !== null}
      busy={busy}
      title={t('admin.users.rename')}
      subtitle={user?.email}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Notice tone="info">{t('admin.users.renameHelp')}</Notice>

      <div className="form-grid form-grid--full">
        <TextInput
          label={t('admin.users.fullName')}
          required
          maxLength={150}
          error={form.formState.errors.fullName?.message}
          {...form.register('fullName', {
            // The server refuses a name of nothing but spaces too; saying so here keeps the dialog open
            // with the reason on the field, rather than a round trip for a 400.
            validate: (value) => value.trim() !== '' || t('errors.required'),
          })}
        />
      </div>
    </Dialog>
  )
}

/**
 * Changes a user's role — Addendum No. 3, Point 7: how a member becomes a Gestor Individual, or stops
 * being one. `PATCH /users/{id}` has always accepted `role`; the portal offered no way to send it.
 * Company administrators and the platform alike: the server guards it with `org:admin`, applies it
 * from the user's next request, and refuses the last administrator (`LAST_ADMIN`), which is answered
 * here in words.
 *
 * What a change into or out of the role does to what the user sees is said before it is made: from
 * then on a Gestor sees only what they create, and one who leaves the role sees the whole company
 * while keeping their documents.
 */
function ChangeRoleDialog({
  user,
  roles,
  onClose,
  onChanged,
}: {
  /** The row whose role is being changed, or null when the dialog is closed. */
  user: { id: string; email: string; role: Role } | null
  roles: readonly Role[]
  onClose: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const form = useForm<{ role: Role }>({ defaultValues: { role: 'ORG_USER' } })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const role = form.watch('role')

  useEffect(() => {
    if (user) {
      form.reset({ role: user.role })
      setError(null)
    }
  }, [user, form])

  const consequence =
    user && role !== user.role
      ? role === 'GESTOR_INDIVIDUAL'
        ? t('admin.users.changeRoleIntoGestor')
        : user.role === 'GESTOR_INDIVIDUAL'
          ? t('admin.users.changeRoleOutOfGestor')
          : null
      : null

  async function submit(values: { role: Role }) {
    if (!user) return
    if (values.role === user.role) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.PATCH('/api/v1/users/{userId}', { params: { path: { userId: user.id } }, body: { role: values.role } }),
      )
      onChanged()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={user !== null}
      busy={busy}
      title={t('admin.users.changeRole')}
      subtitle={user?.email}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={busy} disabled={busy} onClick={() => void form.handleSubmit(submit)()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="form-grid form-grid--full">
        <SelectInput label={t('admin.users.role')} required {...form.register('role')}>
          {roles.map((value) => (
            <option key={value} value={value}>
              {t(roleKey(value))}
            </option>
          ))}
        </SelectInput>
      </div>
      <p className="small muted">{t(`role.description.${role}`)}</p>
      {consequence ? <Notice tone="warning">{consequence}</Notice> : null}
    </Dialog>
  )
}

export function CreateUserDialog({
  open,
  roles,
  locale,
  onClose,
  onCreated,
}: {
  open: boolean
  roles: readonly Role[]
  /** The organisation's invitation language. Undefined while the fetch is in flight. */
  locale: 'es' | 'en' | undefined
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  // The default is taken from the offered list, never named again here. A hardcoded
  // `ORG_USER` would survive as the form's value while the select rendered its first
  // option, and the POST would carry a role nobody chose.
  const form = useForm<UserFormValues>({
    defaultValues: { fullName: '', email: '', role: roles[0] ?? 'ORG_USER', locale: locale ?? 'es' },
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const role = form.watch('role')

  // The dialog is rendered unconditionally by the parent — only `open` toggles — so
  // react-hook-form's `defaultValues` above is captured once, at first mount, before the
  // organisation's locale has ever resolved. RHF's `values` option would re-sync the whole
  // form (not just this field) on every reference change, which risks clobbering a
  // partially-filled fullName/email if the organisation query refetches while the dialog is
  // open. An effect that touches only the `locale` field, and only when the dialog opens or
  // the fetched locale changes, does not have that failure mode.
  useEffect(() => {
    if (open && locale) form.setValue('locale', locale)
  }, [open, locale, form])

  async function submit(values: UserFormValues) {
    setBusy(true)
    setError(null)
    try {
      await unwrap(api.POST('/api/v1/users', { body: values }))
      // Reset to the *current* organisation locale, not the mount-time default — without
      // this, the field an admin sees is right the first time and wrong for every user
      // created after it in the same session.
      form.reset({ fullName: '', email: '', role: roles[0] ?? 'ORG_USER', locale: locale ?? 'es' })
      onCreated()
      onClose()
    } catch (caught) {
      const onAddress = undeliverableEmailMessage(caught, t) ?? invalidEmailMessage(caught, t, 'body/email')
      if (onAddress) form.setError('email', { message: onAddress }, { shouldFocus: true })
      else setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={t('admin.users.create')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() => void form.handleSubmit(submit)()}
          >
            {t('common.create')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Notice tone="info">{t('admin.users.inviteHelp')}</Notice>

      <div className="form-grid form-grid--full">
        <TextInput
          label={t('admin.users.fullName')}
          required
          error={form.formState.errors.fullName?.message}
          {...form.register('fullName', { required: t('errors.required') })}
        />
        <TextInput
          label={t('admin.users.email')}
          type="email"
          required
          error={form.formState.errors.email?.message}
          {...form.register('email', emailAddressRules(t))}
        />
        <SelectInput label={t('admin.users.role')} required {...form.register('role')}>
          {roles.map((value) => (
            <option key={value} value={value}>
              {t(roleKey(value))}
            </option>
          ))}
        </SelectInput>
        <SelectInput label={t('admin.users.locale')} {...form.register('locale')}>
          <option value="es">Español</option>
          <option value="en">English</option>
        </SelectInput>
      </div>

      <p className="small muted">{t(`role.description.${role}`)}</p>
    </Dialog>
  )
}
